import { message } from "antd";
import moment from "moment";

// 到期日 3 天內 (含今天) 視為即將到期
const SOON_DAYS = 3;

export function dueStatus(dueDate, state, today = moment()) {
  if (!dueDate || state !== "new") {
    return null;
  }
  const due = moment(dueDate, "YYYY-MM-DD");
  const base = moment(today).startOf("day");
  if (due.isBefore(base)) {
    return "overdue";
  }
  return due.diff(base, "days") <= SOON_DAYS ? "soon" : null;
}

// 待辦頁的頁籤。「待我處理」整併了原本的「近三天」與「未完成」 -
// 它是依錨點分組的完整未完成清單 (含「之後」段)，所以不再需要第二個未完成頁籤。
export const TAB_MINE = "mine";
export const TAB_IGNORED = "ignored";
export const TAB_COMPLETED = "completed";
export const TAB_ALL = "all";

// 「全部」對所有人開放，但它的內容依身分不同：後端對非管理員的 scope=all
// 會套 visibleTo，所以他們看到的是「指派給我 + 我建立的 + 沒有部門或屬於我部門的無主工作」，
// 管理員才是真的全部。
//
// 它必須對非管理員開放：一筆「我建立、指派給同事、未完成」的待辦在
// 待我處理 (要負責人是我，或沒有負責人)、已忽略／已完成 (狀態不符)
// 都查不到，沒有這個頁籤它就從建立者的畫面上消失，而他其實有權限改派或催辦。
const TABS = [
  { key: TAB_MINE, label: "待我處理" },
  { key: TAB_IGNORED, label: "已忽略" },
  { key: TAB_COMPLETED, label: "已完成" },
  { key: TAB_ALL, label: "全部" },
];

export function tabsFor() {
  return TABS.map(({ key, label }) => ({ key, label }));
}

// 每一段預設只渲染這麼多筆，超過時顯示「還有 N 筆，展開」。
// 移除「可認領」區塊之後「待我處理」是唯一的工作清單，正式站的「今天」有 144 筆 -
// 一次渲染會把明天／後天／之後擠到看不見，而段落標題仍顯示真實件數。
export const GROUP_RENDER_LIMIT = 20;

// 「已忽略」與「已完成」的範圍：管理員看全部，其他人看「與我有關的」
// (我處理的 + 指派給我的 + 我建立的)。只比 assignee_id 會讓執行長近 60 天
// 處理的 221 筆從他的「已完成」消失 - 那些沒有負責人。
export function historyScope(isAdmin) {
  return isAdmin ? "all" : "mine";
}

// 待辦有增減或狀態變更時通知側邊欄重新抓角標數量
export const TODOS_CHANGED_EVENT = "todos:changed";

// axios 攔截器只會 re-reject 不會提示，由呼叫端顯示錯誤
export function showRequestError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 404) {
    message.error("這筆待辦已不存在或已改派");
    return;
  }

  // Laravel 驗證失敗的 data.message 是制式的英文 "The given data was invalid."，
  // 真正有用的在 data.errors —— 取第一則顯示。
  // 不這樣做，刻意寫的中文 422 訊息一條都看不到。
  const first = data?.errors ? Object.values(data.errors)[0] : null;
  if (Array.isArray(first) && first.length > 0) {
    message.error(first[0]);
    return;
  }

  message.error(data?.message || "操作失敗，請稍後再試");
}

// 子項目的 404 有兩種來源 (看不到那筆待辦、子項目不屬於那筆待辦)，
// 與「待辦不存在」是不同的事 —— 沿用 showRequestError 會把原因講錯
export function showChecklistError(error) {
  if (error?.response?.status === 404) {
    message.error("這一項已不存在，畫面已更新");
    return;
  }
  showRequestError(error);
}

export function notifyTodosChanged() {
  window.dispatchEvent(new Event(TODOS_CHANGED_EVENT));
}

// 下拉只列啟用中的部門，但編輯一筆指向停用部門的待辦時要把該部門補回去，
// 否則 antd 的 Select 找不到對應選項，會顯示成裸的 id
export function departmentOptionsFor(options, todo) {
  const list = (options || []).map((d) => ({ value: d.id, label: d.name }));
  const current = todo && todo.department;

  if (current && !list.some((o) => o.value === current.id)) {
    list.push({ value: current.id, label: current.name });
  }

  return list;
}

// 後端回的是 MySQL time 的原樣 HH:MM:SS，不是送出時的 HH:mm。
// 用 'HH:mm' 解析 '09:00:00' 會得到 Invalid date，所以解析格式必須是 'HH:mm:ss'。
export function formatStartTime(value) {
  if (!value) {
    return null;
  }
  const m = moment(value, "HH:mm:ss", true);
  return m.isValid() ? m.format("HH:mm") : null;
}

// 絕大多數待辦沒有清單，所以 total 為 0 時回 null —— 讓畫面不要多一個 0/0
export function checklistLabel(checklist) {
  if (!checklist || !checklist.total) {
    return null;
  }
  return `${checklist.done}/${checklist.total}`;
}

// 空清單不算「全部完成」—— 否則沒有檢查清單的待辦 (絕大多數) 都會被染成綠色。
// 這是不能簡化成 done === total 的原因。
export function checklistAllDone(checklist) {
  return !!checklist && checklist.total > 0 && checklist.done === checklist.total;
}

// 兩個條件都要：只看 id 為空會把任何形狀異常的資料當成虛擬項目，
// 讓一筆壞掉的真實待辦變成按不下任何按鈕、只剩「開始」的鬼魂
export function isOccurrence(todo) {
  return !!todo && !todo.id && !!todo.template_id;
}

// ISO 星期：一=1 … 日=7。後端存的就是這個編號，兩邊必須一致
const WEEKDAY_NAMES = ["一", "二", "三", "四", "五", "六", "日"];

// 表格的一整欄都靠它，所以壞資料回「—」而不是丟錯 ——
// 一筆規則有問題的範本不該讓整張表白掉
export function recurrenceLabel(template) {
  const recurrence = template ? template.recurrence : null;

  if (recurrence === "daily") {
    return "每天";
  }

  const days = template && Array.isArray(template.recurrence_days) ? template.recurrence_days.map(Number) : [];
  if (days.length === 0 || days.some((d) => !Number.isInteger(d))) {
    return "—";
  }

  if (recurrence === "weekly") {
    if (days.some((d) => d < 1 || d > 7)) {
      return "—";
    }
    return `每週${days.map((d) => WEEKDAY_NAMES[d - 1]).join("、")}`;
  }

  if (recurrence === "monthly") {
    if (days.some((d) => d < 1 || d > 31)) {
      return "—";
    }
    return `每月 ${days.join("、")} 日`;
  }

  return "—";
}

// 後端的四種合規狀態。未知值直接顯示原字串而不是空白 ——
// 將來後端多一種狀態時，畫面會露出它而不是看起來少了一列
const COMPLIANCE_STATES = {
  completed: { label: "已完成", color: "green" },
  ignored: { label: "已略過", color: undefined },
  pending: { label: "未結案", color: "blue" },
  missing: { label: "沒做", color: "red" },
};

export function complianceStateMeta(state) {
  if (!state) {
    return { label: "—", color: undefined };
  }
  return COMPLIANCE_STATES[state] || { label: state, color: undefined };
}
